import * as React from 'react';
import { cn } from '../../lib/utils';

export interface TableShellProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** When true, the header is sticky inside a scrollable container. */
  stickyHeader?: boolean;
  /** Density. `compact` = h-9 rows; `comfortable` = h-11. */
  density?: 'compact' | 'comfortable';
}

/**
 * TableShell — chrome for a tabular view.
 *
 * Owns the wrapper, borders, scrolling behavior, and density. The table
 * *content* (THead/TBody) is composed by the consumer using the primitive
 * subcomponents below or shadcn's Table primitives. This is intentionally
 * thinner than a full DataTable — keep it composable.
 */
export const TableShell = React.forwardRef<
  HTMLDivElement,
  TableShellProps & { children?: React.ReactNode }
>(({ stickyHeader, density = 'comfortable', className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-density={density}
      className={cn(
        'relative overflow-hidden rounded-lg border border-border bg-card',
        className,
      )}
      {...props}
    >
      <div className={cn(stickyHeader && 'max-h-[640px] overflow-y-auto')}>
        {children}
      </div>
    </div>
  );
});
TableShell.displayName = 'TableShell';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  ),
);
Table.displayName = 'Table';

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      'bg-muted/40 text-xs font-medium uppercase tracking-wider text-muted-foreground [&_tr]:border-b [&_tr]:border-border',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('divide-y divide-border [&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-border transition-colors hover:bg-muted/40 data-[state=selected]:bg-muted',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-9 px-3 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'px-3 py-2.5 align-middle text-sm text-foreground [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';
